import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  IconButton,
  Box,
  Chip,
  Avatar,
  Stack,
  Button,
  Checkbox,
} from '@mui/material';
import Tooltip from '../shared/LibraryTooltip';
import {
  StarBorder as FavoriteBorderIcon,
  Star as FavoritedIcon,
  ShareOutlined as ShareIcon,
  FolderOutlined as FolderIcon,
  LockOutlined as LockIcon,
} from '@mui/icons-material';
import {
  libraryCardTitleSlotSx,
  libraryCardDescriptionSlotSx,
  libraryCardMetaSx,
} from '../shared/prompt-display/libraryCardSlotStyles';
import { libraryCardSx } from '../../designTokens';
import { getUserDisplayName, getUserInitials } from '../../utils/userUtils';
import {
  getContextCategoryLabel,
  getContextCategoryBadgeSx,
  getContextSecondaryBadgeSx,
} from '../../constants/contextFolders';
import { formatProjectUpdatedLabel } from '../../constants/projectCategories';
import { PRODUCT_TERMS } from '../../constants/productTerminology';

const truncateDescription = (text, max = 140) => {
  if (!text) return '';
  if (text.length <= max) return text;
  return `${text.slice(0, max - 1).trimEnd()}…`;
};

const formatCompactCount = (value) => {
  const num = Number(value) || 0;
  if (num >= 1_000_000) {
    return `${(num / 1_000_000).toFixed(1).replace(/\.0$/, '')}m`;
  }
  if (num >= 1_000) {
    return `${(num / 1_000).toFixed(1).replace(/\.0$/, '')}k`;
  }
  return String(num);
};

const looksLikeRawUserId = (value) => {
  const text = String(value || '').trim();
  if (!text) return false;
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(text)
    || /^[0-9a-f]{20,}$/i.test(text);
};

const ContextCard = ({
  context,
  onContextClick,
  onFavorite,
  onShare,
  onCollectionClick,
  onRequestAccess,
  compact = false,
  fullWidthOnMobile = false,
  selectable = false,
  selected = false,
  onSelect,
}) => {
  const hasAccess = context.hasAccess !== false;
  const explicitAuthorName = context.author?.name && context.author.name !== 'Unknown' && !looksLikeRawUserId(context.author.name)
    ? context.author.name
    : '';
  const authorUser = {
    ...(context.author || {}),
    name: explicitAuthorName || context.userName || context.ownerName || context.createdByName || context.creatorName,
    email: context.author?.email || context.userEmail,
    id: context.author?.id || context.userId,
  };
  const resolvedAuthorName = getUserDisplayName(authorUser);
  const authorName = looksLikeRawUserId(resolvedAuthorName) ? 'Unknown User' : resolvedAuthorName;
  const descriptionPreview = truncateDescription(context.description || '');
  const descriptionTooltip = (context.description || '').trim();
  const updatedLabel = formatProjectUpdatedLabel(context.updatedAt || context.createdAt);
  const knowledgeType = getContextCategoryLabel(context);
  const cardPadding = compact ? 1.25 : 1.75;

  const useCount = context.stats?.usageCount || context.usage || 0;
  const favoriteCount = context.stats?.favorites || context.favorites || 0;

  const handleCollectionChipAuxClick = (e) => {
    const id = context.collection?.id || context.collectionId;
    if (e.button !== 1 || !id) return;
    e.preventDefault();
    e.stopPropagation();
    window.open(`${window.location.origin}/collections/${encodeURIComponent(id)}`, '_blank', 'noopener,noreferrer');
  };

  const handleCardAuxClick = (e) => {
    if (e.button !== 1 || !hasAccess || !context?.id) return;
    e.preventDefault();
    window.open(`${window.location.origin}/contexts/${context.id}`, '_blank', 'noopener,noreferrer');
  };

  return (
    <Card
      sx={{
        ...libraryCardSx,
        height: '100%',
        cursor: hasAccess ? 'pointer' : 'default',
        opacity: hasAccess ? 1 : 0.85,
        position: 'relative',
        ...(compact && {
          maxWidth: fullWidthOnMobile ? 'none' : { xs: 360, sm: 'none' },
          mx: fullWidthOnMobile ? 0 : { xs: 'auto', sm: 0 },
          width: fullWidthOnMobile ? '100%' : undefined,
        }),
        ...(!hasAccess && {
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.02)',
            pointerEvents: 'none',
            zIndex: 1,
          },
        }),
      }}
      onClick={() => hasAccess && onContextClick?.(context)}
      onAuxClick={handleCardAuxClick}
    >
      <CardContent
        sx={{
          flexGrow: 1,
          p: cardPadding,
          position: 'relative',
          zIndex: 2,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: compact ? 0.625 : 0.875, gap: 0.75 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0, flexWrap: 'wrap' }}>
            {knowledgeType && (
              <Chip
                label={knowledgeType}
                size="small"
                variant="outlined"
                sx={getContextCategoryBadgeSx(knowledgeType)}
              />
            )}
            {(context.collection || context.collectionId) && hasAccess && (
              <Chip
                icon={<FolderIcon sx={{ fontSize: '12px !important', color: '#94A3B8 !important' }} />}
                label={
                  context.collection?.title ||
                  context.collection?.name ||
                  context.collectionName ||
                  'Project'
                }
                size="small"
                variant="outlined"
                onClick={(e) => {
                  e.stopPropagation();
                  if (onCollectionClick) {
                    if (context.collection) {
                      onCollectionClick(context.collection);
                    } else if (context.collectionId) {
                      onCollectionClick({
                        id: context.collectionId,
                        title: context.collectionName,
                        name: context.collectionName,
                      });
                    }
                  }
                }}
                onAuxClick={handleCollectionChipAuxClick}
                sx={getContextSecondaryBadgeSx({ clickable: true })}
              />
            )}
          </Box>

          {hasAccess && (
            <Stack direction="row" spacing={0} sx={{ flexShrink: 0, mt: -0.25 }}>
              {selectable && (
                <Tooltip title={selected ? 'Remove from selection' : 'Add to selection'}>
                  <Checkbox
                    checked={selected}
                    size="small"
                    inputProps={{ 'aria-label': `${selected ? 'Remove from selection' : 'Add to selection'}: ${context.name || context.title || 'Untitled knowledge'}` }}
                    onClick={(event) => event.stopPropagation()}
                    onChange={(event) => {
                      event.stopPropagation();
                      onSelect?.(context, event.target.checked);
                    }}
                    sx={{
                      p: 0.25,
                      mr: 0.125,
                      color: 'text.disabled',
                      '&.Mui-checked': { color: 'primary.main' },
                      '& .MuiSvgIcon-root': { fontSize: 18 },
                    }}
                  />
                </Tooltip>
              )}
              {onFavorite && (
                <IconButton
                  size="small"
                  aria-label="Favorite"
                  onClick={(e) => {
                    e.stopPropagation();
                    onFavorite(context.id);
                  }}
                  sx={{ p: 0.375, '& .MuiSvgIcon-root': { fontSize: 16 } }}
                >
                  {context.isFavorited
                    ? <FavoritedIcon sx={{ color: '#d4a017' }} />
                    : <FavoriteBorderIcon sx={{ color: 'text.disabled' }} />}
                </IconButton>
              )}
              {onShare && (
                <IconButton
                  size="small"
                  aria-label="Share"
                  onClick={(e) => {
                    e.stopPropagation();
                    onShare(context);
                  }}
                  sx={{ p: 0.375, '& .MuiSvgIcon-root': { fontSize: 16 } }}
                >
                  <ShareIcon sx={{ color: 'text.disabled' }} />
                </IconButton>
              )}
            </Stack>
          )}
        </Box>

        <Box sx={{ mb: compact ? 0.625 : 0.875 }}>
          <Typography component="h3" variant="h6" sx={libraryCardTitleSlotSx()}>
            {context.name || PRODUCT_TERMS.knowledge.untitled}
          </Typography>
        </Box>

        <Tooltip
          title={descriptionTooltip}
          placement="top"
          arrow
          disableHoverListener={!descriptionTooltip}
        >
          <Typography
            component="p"
            variant="body2"
            sx={{
              ...libraryCardDescriptionSlotSx(compact),
              mb: compact ? 0.625 : 1,
            }}
          >
            {descriptionPreview || '\u00a0'}
          </Typography>
        </Tooltip>

        <Typography
          component="p"
          variant="body2"
          sx={{
            mb: compact ? 0.75 : 1.25,
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'baseline',
            gap: 0.5,
            rowGap: 0.25,
            fontSize: compact ? '0.6875rem' : '0.75rem',
            lineHeight: compact ? 1.35 : 1.5,
          }}
        >
          <Box
            component="span"
            sx={{ fontWeight: 400, color: 'rgb(100, 116, 139)' }}
            aria-label={`Uses: ${useCount}`}
          >
            {formatCompactCount(useCount)} Uses
          </Box>
          <Box component="span" sx={{ color: 'rgb(203, 213, 225)', userSelect: 'none' }}>
            ·
          </Box>
          <Box
            component="span"
            sx={{ fontWeight: 400, color: 'rgb(100, 116, 139)' }}
            aria-label={`Favorites: ${favoriteCount}`}
          >
            {formatCompactCount(favoriteCount)} Favorites
          </Box>
        </Typography>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 0.75,
            mt: 'auto',
            pt: compact ? 0.125 : 0.375,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.625, minWidth: 0 }}>
            <Avatar
              sx={{
                width: compact ? 18 : 20,
                height: compact ? 18 : 20,
                fontSize: '0.59375rem',
                bgcolor: 'rgba(29, 78, 216, 0.12)',
                color: 'primary.main',
                flexShrink: 0,
              }}
            >
              {getUserInitials(authorUser)}
            </Avatar>
            <Typography
              component="span"
              noWrap
              sx={{
                fontSize: compact ? '10.5px' : '11.5px',
                fontWeight: 500,
                color: 'rgb(100, 116, 139)',
                lineHeight: 1.4,
                minWidth: 0,
              }}
            >
              {authorName}
            </Typography>
          </Box>
          {updatedLabel && (
            <Typography component="span" noWrap sx={{ ...libraryCardMetaSx, flexShrink: 0 }}>
              Updated {updatedLabel}
            </Typography>
          )}
        </Box>

        {!hasAccess && onRequestAccess && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<LockIcon />}
            onClick={(e) => {
              e.stopPropagation();
              onRequestAccess(context);
            }}
            sx={{ width: '100%', mt: 2 }}
          >
            Request Access
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

export default ContextCard;
